'use client'

/**
 * SearchSuggest — THE one search-suggestions engine for the whole site
 * (W4.1 merge, 2026-07-22). Absorbs the orphaned components/SmartSearch.tsx
 * (deleted) and the inline dropdown SearchFilters.tsx used to carry.
 *
 * - `useSearchSuggest` fetches the cached GET /api/search/suggestions route
 *   (CDN s-maxage) with a 90ms debounce and an in-memory client cache — the
 *   SmartSearch data path, which replaced the uncacheable server-action POST.
 * - `flattenSuggestions` turns the grouped result into one keyboard-navigable
 *   list covering EVERY category the backend returns — addresses included
 *   (the "3480" class: the old dropdown never rendered them).
 * - `SearchSuggestPanel` (./SearchSuggestPanel.tsx) renders the grouped
 *   dropdown. It lives in its own module so hook-only consumers (the site
 *   chrome, the home hero) never pull cmdk into every page's JS (perf budget,
 *   2026-11-04). Do not re-export it from here.
 *
 * Consumers own their input element and keyboard wiring; the panel is
 * aria-activedescendant-compatible (`${idPrefix}-item-${index}`).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { SearchSuggestionsResult } from '@/app/actions/listings'
import { cityPagePath } from '@/lib/slug'
import { communityPagePath } from '@/lib/community-slug'
import { ALL_TYPES_BASIS, forSaleText } from '@/lib/listing-counts'

export const SUGGEST_MIN_QUERY_LENGTH = 2
const DEBOUNCE_MS = 90

export const EMPTY_SUGGESTIONS: SearchSuggestionsResult = {
  addresses: [],
  cities: [],
  subdivisions: [],
  neighborhoods: [],
  zips: [],
  brokers: [],
  reports: [],
  pages: [],
}

export type SuggestKind =
  | 'address'
  | 'city'
  | 'subdivision'
  | 'neighborhood'
  | 'zip'
  | 'broker'
  | 'report'
  | 'page'

export type SuggestItem = {
  kind: SuggestKind
  label: string
  sublabel?: string
  href: string
  /** Structured fields so filter-bar consumers can apply filters in place. */
  city?: string
  subdivisionName?: string
  postalCode?: string
}

export const SUGGEST_GROUP_LABELS: Record<SuggestKind, string> = {
  address: 'Addresses',
  city: 'Cities',
  subdivision: 'Communities',
  neighborhood: 'Neighborhoods',
  zip: 'Zip codes',
  broker: 'Agents and brokers',
  report: 'Market reports',
  page: 'Pages and guides',
}

/** Per-category render caps (dropdown stays scannable). */
const CAPS = {
  addresses: 8,
  cities: 5,
  subdivisions: 8,
  neighborhoods: 5,
  zips: 5,
  brokers: 5,
  reports: 3,
  pages: 6,
} as const

/**
 * Flatten the grouped backend result into ONE ordered list. Every category the
 * backend returns is represented here — a category missing from this function
 * is a rendering bug (contract-tested).
 *
 * Ordering contract (buyer-journey audit F6, 2026-07-29): NAMED PLACES rank
 * above street addresses — neighborhoods, cities, subdivisions, THEN
 * addresses, THEN zips (brokers / reports / pages trail as before). The old
 * addresses-first order buried a matched neighborhood under up to 17 street
 * addresses that merely contained the query words. A buyer typing a place
 * name wants the place. Backend best-match order inside each group is
 * preserved untouched.
 */
export function flattenSuggestions(s: SearchSuggestionsResult | null): SuggestItem[] {
  if (!s) return []
  const items: SuggestItem[] = []
  for (const n of (s.neighborhoods ?? []).slice(0, CAPS.neighborhoods)) {
    items.push({ kind: 'neighborhood', label: n.neighborhoodName, sublabel: n.cityName, href: n.href })
  }
  for (const c of (s.cities ?? []).slice(0, CAPS.cities)) {
    items.push({
      kind: 'city',
      label: c.city,
      // W1: the count says what it counts (getForSaleCount, every home type).
      sublabel: c.count > 0 ? forSaleText({ count: c.count, basis: ALL_TYPES_BASIS }) : undefined,
      href: cityPagePath(c.city),
      city: c.city,
    })
  }
  for (const sub of (s.subdivisions ?? []).slice(0, CAPS.subdivisions)) {
    items.push({
      kind: 'subdivision',
      label: sub.subdivisionName,
      sublabel: sub.city,
      href: communityPagePath(sub.city, sub.subdivisionName),
      city: sub.city,
      subdivisionName: sub.subdivisionName,
    })
  }
  for (const a of (s.addresses ?? []).slice(0, CAPS.addresses)) {
    items.push({ kind: 'address', label: a.label, href: a.href })
  }
  for (const z of (s.zips ?? []).slice(0, CAPS.zips)) {
    items.push({
      kind: 'zip',
      label: z.postalCode,
      sublabel: z.city,
      href: z.href,
      postalCode: z.postalCode,
    })
  }
  for (const b of (s.brokers ?? []).slice(0, CAPS.brokers)) {
    items.push({ kind: 'broker', label: b.label, href: b.href })
  }
  for (const r of (s.reports ?? []).slice(0, CAPS.reports)) {
    items.push({ kind: 'report', label: r.label, href: r.href })
  }
  for (const p of (s.pages ?? []).slice(0, CAPS.pages)) {
    items.push({
      kind: 'page',
      label: p.label,
      sublabel: p.kind === 'guide' ? 'Guide' : p.kind === 'blog' ? 'Blog' : undefined,
      href: p.href,
    })
  }
  return items
}

/**
 * Debounced (90ms), client-cached fetch of the cached GET suggestions route.
 * Latest-query-wins: out-of-order responses never clobber newer input.
 */
export function useSearchSuggest(query: string): {
  suggestions: SearchSuggestionsResult | null
  loading: boolean
  /** The trimmed query `suggestions` answer. Until a newer answer lands they are the last query's. */
  answeredQuery: string
} {
  const [suggestions, setSuggestions] = useState<SearchSuggestionsResult | null>(null)
  const [answeredQuery, setAnsweredQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const cacheRef = useRef<Map<string, SearchSuggestionsResult>>(new Map())
  const latestRef = useRef('')

  const fetchSuggestions = useCallback(async (q: string) => {
    const cacheKey = q.toLowerCase()
    const cached = cacheRef.current.get(cacheKey)
    if (cached) {
      setSuggestions(cached)
      setAnsweredQuery(q)
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const response = await fetch(`/api/search/suggestions?q=${encodeURIComponent(q)}`)
      const result = (await response.json()) as SearchSuggestionsResult
      // Degraded = a backing read failed server-side. Render what came back,
      // but never pin it — the next keystroke (or retype) retries fresh.
      if (!result.degraded) cacheRef.current.set(cacheKey, result)
      if (latestRef.current === q) {
        setSuggestions(result)
        setAnsweredQuery(q)
      }
    } catch {
      if (latestRef.current === q) {
        setSuggestions(null)
        setAnsweredQuery(q)
      }
    } finally {
      if (latestRef.current === q) setLoading(false)
    }
  }, [])

  useEffect(() => {
    const q = query.trim()
    latestRef.current = q
    if (q.length < SUGGEST_MIN_QUERY_LENGTH) {
      setSuggestions(null)
      setAnsweredQuery(q)
      setLoading(false)
      return
    }
    const t = setTimeout(() => fetchSuggestions(q), DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [query, fetchSuggestions])

  return { suggestions, loading, answeredQuery }
}
